import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Sparkles, CheckCircle2, Circle, ArrowRight, X, Loader2, AlertCircle, RefreshCw } from 'lucide-react';
import useOnboardingSteps from '../hooks/useOnboardingSteps';
import useJobSyncStatus from '../hooks/useJobSyncStatus';
import { syncJobsNow } from '../services/tenantService';
import { getTimeAgo } from '../utils/dateUtils';
import { getStageColor, getMotionClasses } from '../utils/stageColors';
import {
  trackOnboardingFlowStarted,
  trackOnboardingStepEvent,
  trackOnboardingSyncEvent,
} from '../utils/analyticsHelper';
import {
  ONBOARDING_STEP_VIEWED,
  ONBOARDING_STEP_COMPLETED,
  ONBOARDING_STEP_SKIPPED,
  ONBOARDING_SYNC_STARTED,
  ONBOARDING_SYNC_COMPLETED,
  ONBOARDING_SYNC_FAILED,
} from '../constants/analyticsEvents';

const HIGHLIGHT_CLASSES = ['ring-2', 'ring-primary-500', 'ring-offset-2', 'animate-pulse'];

const buildChecklistStorageKey = (userId, tenantId) => `onboarding-checklist:${userId}:${tenantId || 'none'}`;

const defaultChecklistPrefs = { seen: false, dismissedUntil: null };

const safeParseChecklistPrefs = (value) => {
  if (!value) return { ...defaultChecklistPrefs };
  try {
    const parsed = JSON.parse(value);
    return { ...defaultChecklistPrefs, ...(parsed || {}) };
  } catch {
    return { ...defaultChecklistPrefs };
  }
};

export function OnboardingChecklist() {
  const location = useLocation();
  const navigate = useNavigate();
  const {
    loading,
    steps,
    isEligible,
    isPublicAuthPath,
    onboardingPrefs,
    onboardingIsComplete,
    completedCount,
    allCompleted,
    user,
    tenant,
    refreshSteps,
    setFocusedStepId,
  } = useOnboardingSteps({
    enabled: true,
    pathname: location.pathname,
    skipPublicAuthRoutes: true,
    autoRefreshMs: 15000,
    refreshOnFocus: true,
  });

  const [checklistPrefs, setChecklistPrefs] = useState(defaultChecklistPrefs);
  const [isOpen, setIsOpen] = useState(false);
  const [activeStepId, setActiveStepId] = useState(null);
  const [isTriggeringSync, setIsTriggeringSync] = useState(false);
  const [syncError, setSyncError] = useState(null);

  const highlightedElementRef = useRef(null);
  const clearHighlightTimerRef = useRef(null);
  const prevStepsRef = useRef([]);

  const onSyncStarted = useCallback(() => {
    setSyncError(null);
    trackOnboardingSyncEvent(ONBOARDING_SYNC_STARTED, {
      userId: user?._id,
      role: user?.role,
      tenantId: tenant?._id,
      syncType: 'jobs',
    });
  }, [user?._id, user?.role, tenant?._id]);

  const onSyncCompleted = useCallback(({ eventData, previousJobs }) => {
    const durationMs = previousJobs?.syncStartTime ? Date.now() - previousJobs.syncStartTime : undefined;
    const result = eventData?.result || {};
    const itemCount = Number(result.new || 0) + Number(result.updated || 0);

    setIsTriggeringSync(false);
    setSyncError(null);

    trackOnboardingSyncEvent(ONBOARDING_SYNC_COMPLETED, {
      userId: user?._id,
      role: user?.role,
      tenantId: tenant?._id,
      syncType: 'jobs',
      durationMs,
      itemCount,
    });

    refreshSteps({ showLoading: false, forceRefresh: true });
  }, [user?._id, user?.role, tenant?._id, refreshSteps]);

  const onSyncFailed = useCallback(({ eventData, previousJobs }) => {
    const durationMs = previousJobs?.syncStartTime ? Date.now() - previousJobs.syncStartTime : undefined;
    const errorMessage = eventData?.error || 'Sync failed';
    setSyncError(errorMessage);
    setIsTriggeringSync(false);
    trackOnboardingSyncEvent(ONBOARDING_SYNC_FAILED, {
      userId: user?._id,
      role: user?.role,
      tenantId: tenant?._id,
      syncType: 'jobs',
      durationMs,
      errorCode: eventData?.error || 'SYNC_ERROR',
    });
  }, [user?._id, user?.role, tenant?._id]);

  const { syncStatus } = useJobSyncStatus({
    enabled: isEligible,
    pollMs: 10000,
    onSyncStarted,
    onSyncCompleted,
    onSyncFailed,
  });

  useEffect(() => {
    if (!isTriggeringSync) return;

    const jobsSync = syncStatus?.jobs;
    if (!jobsSync || jobsSync.isSyncing) return;

    setIsTriggeringSync(false);
    if (jobsSync.lastError) {
      setSyncError(jobsSync.lastError);
    }
  }, [
    isTriggeringSync,
    syncStatus?.jobs?.isSyncing,
    syncStatus?.jobs?.lastError,
    syncStatus?.jobs?.lastSyncTime,
  ]);

  const checklistStorageKey = useMemo(
    () => (isEligible ? buildChecklistStorageKey(user?._id, tenant?._id) : null),
    [isEligible, user?._id, tenant?._id]
  );

  const clearHighlight = useCallback(() => {
    if (clearHighlightTimerRef.current) {
      clearTimeout(clearHighlightTimerRef.current);
      clearHighlightTimerRef.current = null;
    }
    if (highlightedElementRef.current) {
      highlightedElementRef.current.classList.remove(...HIGHLIGHT_CLASSES);
      highlightedElementRef.current = null;
    }
  }, []);

  const applyHighlight = useCallback((target) => {
    clearHighlight();
    const element = document.querySelector(`[data-onboarding="${target}"]`);
    if (!element) return;

    element.scrollIntoView({ behavior: 'smooth', block: 'center' });
    element.classList.add(...HIGHLIGHT_CLASSES);
    highlightedElementRef.current = element;

    clearHighlightTimerRef.current = setTimeout(() => {
      clearHighlight();
    }, 4500);
  }, [clearHighlight]);

  const handleSyncJobsNow = useCallback(async () => {
    setIsTriggeringSync(true);
    setSyncError(null);
    
    try {
      const response = await syncJobsNow({ immediate: true, maxPages: 100 });
      if (!response?.success) {
        throw new Error(response?.message || 'Sync failed');
      }
      setIsTriggeringSync(false);
      setSyncError(null);
      refreshSteps({ showLoading: false, forceRefresh: true });
    } catch (error) {
      console.error('Manual sync failed:', error);
      const errorMessage = error.message || 'Failed to trigger sync';
      setSyncError(errorMessage);
      setIsTriggeringSync(false);
      
      trackOnboardingSyncEvent(ONBOARDING_SYNC_FAILED, {
        userId: user?._id,
        role: user?.role,
        tenantId: tenant?._id,
        syncType: 'jobs',
        errorCode: errorMessage,
      });
    }
  }, [user?._id, user?.role, tenant?._id, refreshSteps]);

  useEffect(() => {
    if (!checklistStorageKey) {
      setChecklistPrefs(defaultChecklistPrefs);
      setIsOpen(false);
      return;
    }

    const stored = safeParseChecklistPrefs(localStorage.getItem(checklistStorageKey));
    setChecklistPrefs(stored);

    if (!stored.seen) {
      setIsOpen(true);
      trackOnboardingFlowStarted({
        userId: user?._id,
        role: user?.role,
        tenantId: tenant?._id,
        flowVariant: 'default',
      });
    }
  }, [checklistStorageKey, user?._id, user?.role, tenant?._id]);

  useEffect(() => {
    if (!checklistStorageKey) return;
    localStorage.setItem(checklistStorageKey, JSON.stringify(checklistPrefs));
  }, [checklistPrefs, checklistStorageKey]);

  useEffect(() => {
    if (!activeStepId) return undefined;
    const step = steps.find((item) => item.id === activeStepId);
    if (!step || location.pathname !== step.path) return undefined;

    const timer = setTimeout(() => {
      applyHighlight(step.target);
    }, 220);

    return () => clearTimeout(timer);
  }, [activeStepId, location.pathname, steps, applyHighlight]);

  useEffect(() => () => clearHighlight(), [clearHighlight]);

  useEffect(() => {
    if (!isEligible || steps.length === 0) return;

    const prevSteps = prevStepsRef.current;
    if (prevSteps.length > 0) {
      steps.forEach((step) => {
        const prevStep = prevSteps.find((item) => item.id === step.id);
        if (prevStep && !prevStep.completed && step.completed) {
          trackOnboardingStepEvent(ONBOARDING_STEP_COMPLETED, {
            userId: user?._id,
            role: user?.role,
            tenantId: tenant?._id,
            stepId: step.id,
            flowVariant: 'default',
          });
        }
      });
    }

    prevStepsRef.current = steps;
  }, [steps, isEligible, user?._id, user?.role, tenant?._id]);

  if (isPublicAuthPath || !isEligible || loading || steps.length === 0) {
    return null;
  }

  const skippedUntilDate = onboardingPrefs?.skippedUntil ? new Date(onboardingPrefs.skippedUntil) : null;
  const isTemporarilySkipped = Boolean(onboardingPrefs?.skipped
    && skippedUntilDate
    && !Number.isNaN(skippedUntilDate.getTime())
    && skippedUntilDate > new Date());

  if (isTemporarilySkipped) {
    return null;
  }

  if (!onboardingIsComplete || allCompleted) {
    return null;
  }

  const dismissedUntil = checklistPrefs.dismissedUntil ? new Date(checklistPrefs.dismissedUntil) : null;
  const isDismissed = dismissedUntil && dismissedUntil > new Date();

  const handleHighlightStep = (step) => {
    setChecklistPrefs((prev) => ({ ...prev, seen: true, dismissedUntil: null }));
    setActiveStepId(step.id);
    setFocusedStepId(step.id);

    trackOnboardingStepEvent(ONBOARDING_STEP_VIEWED, {
      userId: user?._id,
      role: user?.role,
      tenantId: tenant?._id,
      stepId: step.id,
      flowVariant: 'default',
    });

    if (location.pathname !== step.path) {
      navigate(step.path);
      return;
    }

    applyHighlight(step.target);
  };

  const dismissForDays = (days) => {
    const until = new Date(Date.now() + days * 24 * 60 * 60 * 1000).toISOString();
    setChecklistPrefs((prev) => ({ ...prev, seen: true, dismissedUntil: until }));
    setIsOpen(false);
    setFocusedStepId(null);
    clearHighlight();

    steps
      .filter((step) => !step.completed)
      .forEach((step) => {
        trackOnboardingStepEvent(ONBOARDING_STEP_SKIPPED, {
          userId: user?._id,
          role: user?.role,
          tenantId: tenant?._id,
          stepId: step.id,
          flowVariant: 'default',
        });
      });
  };

  if (!isOpen) {
    const progressPercent = Math.round((completedCount / steps.length) * 100);
    const buttonStageIndex = progressPercent < 33 ? 0 : progressPercent < 66 ? 1 : 2;
    const buttonStageColor = getStageColor(buttonStageIndex);

    return (
      <button
        type="button"
        onClick={() => {
          setChecklistPrefs((prev) => ({ ...prev, seen: true, dismissedUntil: null }));
          setIsOpen(true);
        }}
        style={{ borderColor: buttonStageColor.base, color: buttonStageColor.dark }}
        className="fixed bottom-5 right-5 z-30 inline-flex items-center gap-2 px-3 py-2 rounded-lg border bg-white shadow-sm hover:bg-gray-50 transition-colors"
      >
        <Sparkles className="w-4 h-4" />
        <span className="text-sm font-medium">
          {isDismissed ? 'Setup Guide' : `Setup ${completedCount}/${steps.length}`}
        </span>
      </button>
    );
  }

  return (
    <div className="fixed bottom-5 right-5 z-30 w-[22rem] bg-white border border-gray-200 rounded-xl shadow-xl">
      <div className="flex items-start justify-between px-4 py-3 border-b border-gray-100">
        <div>
          <p className="text-sm font-semibold text-gray-900">Getting Started</p>
          <p className="text-xs text-gray-500 mt-0.5">Complete these steps to finish workspace setup.</p>
        </div>
        <button
          type="button"
          onClick={() => setIsOpen(false)}
          className="p-1 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
          aria-label="Close onboarding guide"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      <div className="px-4 py-3">
        <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden mb-3">
          {(() => {
            const pct = Math.round((completedCount / steps.length) * 100);
            const stageIdx = pct < 33 ? 0 : pct < 66 ? 1 : 2;
            const stageCol = getStageColor(stageIdx);
            return <div className={`h-2 transition-all ${getMotionClasses('stage-shift')}`} style={{ width: `${pct}%`, backgroundColor: stageCol.base }} />;
          })()}
        </div>

        <div className="space-y-2">
          {steps.map((step, stepIndex) => {
            const showSyncStatus = step.id === 'sync_jobs' && syncStatus?.jobs;
            const jobSync = syncStatus?.jobs;
            const isSyncing = isTriggeringSync || jobSync?.isSyncing;
            const hasError = syncError || jobSync?.lastError;
            const lastSyncTime = jobSync?.lastSyncTime ? new Date(jobSync.lastSyncTime) : null;
            const lastSyncResult = jobSync?.lastSyncResult;
            const newJobs = lastSyncResult?.new || 0;
            const updatedJobs = lastSyncResult?.updated || 0;
            
            const stageIdx = Math.floor((stepIndex / steps.length) * 3);
            const stageCol = getStageColor(Math.min(stageIdx, 2));
            const motionCls = getMotionClasses(step.completed ? 'subtle-pulse' : 'stage-shift');

            return (
              <div key={step.id} className={`rounded-lg border px-3 py-2 transition-colors ${motionCls}`} style={{ borderColor: stageCol.base, backgroundColor: `${stageCol.light}40` }}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-gray-900 flex items-center gap-1.5">
                      {step.completed ? (
                        <CheckCircle2 className="w-4 h-4 flex-shrink-0" style={{ color: stageCol.base }} />
                      ) : (
                        <Circle className="w-4 h-4 text-gray-300 flex-shrink-0" />
                      )}
                      <span className="truncate">{step.title}</span>
                    </p>
                    <p className="text-xs text-gray-500 mt-0.5">{step.description}</p>

                    {showSyncStatus && (
                      <>
                        <div
                          className={`mt-1.5 flex items-center gap-1.5 text-xs ${
                            hasError ? 'text-red-600' : isSyncing ? 'text-blue-600' : lastSyncTime ? 'text-gray-500' : ''
                          }`}
                        >
                          {hasError ? (
                            <>
                              <AlertCircle className="w-3 h-3 flex-shrink-0" />
                              <span>Sync failed: {hasError}</span>
                            </>
                          ) : isSyncing ? (
                            <>
                              <Loader2 className="w-3 h-3 flex-shrink-0 animate-spin" />
                              <span>Syncing jobs...</span>
                            </>
                          ) : lastSyncTime ? (
                            <>
                              <CheckCircle2 className="w-3 h-3 flex-shrink-0 text-green-600" />
                              <span>
                                Synced {getTimeAgo(lastSyncTime)}
                                {(newJobs > 0 || updatedJobs > 0) && (
                                  <span className="ml-1">
                                    ({newJobs > 0 && `${newJobs} new`}
                                    {newJobs > 0 && updatedJobs > 0 && ', '}
                                    {updatedJobs > 0 && `${updatedJobs} updated`})
                                  </span>
                                )}
                              </span>
                            </>
                          ) : null}
                        </div>
                      </>
                    )}
                  </div>
                  {!step.completed && (
                    <>
                      {step.id === 'sync_jobs' ? (
                        <div className="flex gap-2 flex-shrink-0">
                          {hasError && (
                            <button
                              type="button"
                              onClick={handleSyncJobsNow}
                              disabled={isSyncing}
                              className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-red-700 bg-red-50 border border-red-200 rounded-md hover:bg-red-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              <RefreshCw className="w-3 h-3" />
                              Retry
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={handleSyncJobsNow}
                            disabled={isSyncing}
                            style={{ backgroundColor: `${stageCol.light}60`, borderColor: stageCol.base, color: stageCol.dark }}
                            className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium border rounded-md hover:opacity-80 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {isSyncing ? (
                              <>
                                <Loader2 className="w-3 h-3 animate-spin" />
                                Syncing...
                              </>
                            ) : (
                              <>
                                <RefreshCw className="w-3 h-3" />
                                Sync Now
                              </>
                            )}
                          </button>
                        </div>
                      ) : (
                        <button
                          type="button"
                          onClick={() => handleHighlightStep(step)}
                          style={{ backgroundColor: `${stageCol.light}60`, borderColor: stageCol.base, color: stageCol.dark }}
                          className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium border rounded-md hover:opacity-80 transition-colors flex-shrink-0"
                        >
                          Highlight
                          <ArrowRight className="w-3 h-3" />
                        </button>
                      )}
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="px-4 py-3 border-t border-gray-100 flex items-center justify-between gap-2">
        <button
          type="button"
          onClick={() => dismissForDays(14)}
          className="text-xs text-gray-500 hover:text-gray-700 transition-colors"
        >
          Skip for now
        </button>
        <button
          type="button"
          onClick={() => dismissForDays(1)}
          className="text-xs text-primary-600 hover:text-primary-700 font-medium transition-colors"
        >
          Remind me tomorrow
        </button>
      </div>
    </div>
  );
}

export default OnboardingChecklist;
